import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { companiesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../context/ToastContext';
import Button from './ui/Button';
import CompanyLogoManager from './CompanyLogoManager';
import { resolveCompanyLogo, needsLightBackdrop, logoUrl } from '../utils/companyLogo';

// Branding / Firma mea — gestionează self-service logo + nume brand pentru white-label.
// Gating premium: dacă whiteLabelEnabled=false și nu ești SUPER_ADMIN → upgrade-state,
// editarea e blocată. SUPER_ADMIN poate activa/dezactiva flag-ul (decizie comercială).
//
// `company` trebuie să conțină: { id, name, type, whiteLabelEnabled, brandName, logoLight, logoDark }.
// `onChange(partial)` propagă modificările în starea paginii părinte.
const BrandingManager = ({ company, onChange }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { theme } = useTheme();
  const { showToast } = useToast();

  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const enabled = !!company?.whiteLabelEnabled;
  // Editabil dacă serviciul e activ SAU dacă ești SUPER_ADMIN (suport).
  const canEdit = enabled || isSuperAdmin;

  const [brandName, setBrandName] = useState(company?.brandName || '');
  const [savingName, setSavingName] = useState(false);
  const [togglingWL, setTogglingWL] = useState(false);

  const previewLogo = resolveCompanyLogo(company, theme);
  const previewLogoUrl = previewLogo ? logoUrl(previewLogo) : null;
  const previewBackdrop = needsLightBackdrop(company, theme);
  const displayName = (brandName || '').trim() || company?.name || '';

  const handleSaveBrandName = async () => {
    setSavingName(true);
    try {
      const res = await companiesAPI.updateBranding(company.id, { brandName: brandName.trim() });
      onChange?.({ brandName: res.data.brandName });
      showToast(t('branding.brandNameSaved'), 'success');
    } catch (err) {
      showToast(err.response?.data?.error || t('common.error'), 'error');
    } finally {
      setSavingName(false);
    }
  };

  const handleToggleWhiteLabel = async () => {
    const next = !enabled;
    setTogglingWL(true);
    try {
      await companiesAPI.toggleWhiteLabel(company.id, next);
      onChange?.({ whiteLabelEnabled: next });
      showToast(next ? t('branding.enabled') : t('branding.disabled'), 'success');
    } catch (err) {
      showToast(err.response?.data?.error || t('branding.toggleError'), 'error');
    } finally {
      setTogglingWL(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Antet */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            {t('branding.title')}
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
              {t('branding.premiumBadge')}
            </span>
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {t('branding.subtitle')}
          </p>
        </div>
      </div>

      {/* Toggle SUPER_ADMIN — vizibil întotdeauna pentru SUPER_ADMIN */}
      {isSuperAdmin && (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {t('branding.superAdminToggleTitle')}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {t('branding.superAdminToggleDesc')}
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={enabled}
                disabled={togglingWL}
                onChange={handleToggleWhiteLabel}
              />
              <div className="w-11 h-6 bg-slate-300 dark:bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all" />
            </label>
          </div>
        </div>
      )}

      {/* Upgrade-state — când serviciul e dezactivat și NU ești SUPER_ADMIN */}
      {!canEdit && (
        <div className="rounded-lg border-2 border-dashed border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/40">
            <svg
              className="h-6 w-6 text-amber-600 dark:text-amber-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h3 className="text-base font-semibold text-amber-800 dark:text-amber-200">
            {t('branding.lockedTitle')}
          </h3>
          <p className="mt-1.5 text-sm text-amber-700 dark:text-amber-300/90 max-w-md mx-auto">
            {t('branding.lockedDesc')}
          </p>
          <div className="mt-4">
            <a href="mailto:contact@razitech.ro?subject=Activare%20white-label">
              <Button variant="primary" size="sm">
                {t('branding.lockedCta')}
              </Button>
            </a>
            <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
              {t('branding.lockedCtaHint')}
            </p>
          </div>
        </div>
      )}

      {/* Editor branding — doar dacă poate edita */}
      {canEdit && (
        <>
          {/* Logo light + dark (refolosește componenta existentă) */}
          <CompanyLogoManager
            companyId={company.id}
            logoLight={company.logoLight}
            logoDark={company.logoDark}
            onChange={onChange}
          />

          {/* Nume brand */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
              {t('branding.brandNameLabel')}
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={brandName}
                maxLength={120}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder={t('branding.brandNamePlaceholder')}
                className="flex-1 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Button
                variant="outline"
                size="md"
                onClick={handleSaveBrandName}
                loading={savingName}
                disabled={savingName || brandName.trim() === (company?.brandName || '').trim()}
              >
                {t('branding.saveBrandName')}
              </Button>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t('branding.brandNameHint')}
            </p>
          </div>

          {/* Previzualizare antet document */}
          <div className="space-y-1.5">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              {t('branding.previewTitle')}
            </p>
            <div className="flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
              <div
                className={`flex h-14 w-28 items-center justify-center rounded overflow-hidden ${previewBackdrop ? 'bg-slate-100' : ''}`}
              >
                {previewLogoUrl ? (
                  <img
                    src={previewLogoUrl}
                    alt="logo"
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <span className="text-xs text-slate-400 dark:text-slate-500">
                    {t('branding.noLogo')}
                  </span>
                )}
              </div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">
                  {t('branding.previewCompanyLabel')}
                </p>
                <p className="font-bold text-slate-900 dark:text-slate-100 truncate">
                  {displayName.toUpperCase()}
                </p>
              </div>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t('branding.previewHint')}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default BrandingManager;
